import { useEffect, useMemo, useReducer, useState } from "react";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { CloseOutlined, SaveOutlined } from "@mui/icons-material";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import { _sessionManager, UsersApiHelper } from "api/ApiHelper";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { VisibilityIcon, VisibilityOffIcon } from "components/Icons/Icons";
import { useSnackbar } from "notistack";

import { getErrorMessage } from "./helpers";

export const NewUserDialog = ({ member, selectedUser, isEdit, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();

  const generatePassword = useMemo(() => {
    const today = new Date();
    const formattedDate = `${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;
    const userName = member?.last_name?.trim();
    return userName ? userName + "-" + formattedDate : member?.business_name + "-" + formattedDate;
  }, [member]);

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [user, dispatchUser] = useReducer(propUpdateReducer, selectedUser ? selectedUser : member);

  useEffect(() => {
    if (!member?.password && !user?.password) {
      dispatchUser({
        type: "update",
        payload: {
          key: "password",
          value: member?.password || generatePassword,
        },
      });
    }
    if (user.description === undefined || user.description === null) {
      dispatchUser({
        type: "update",
        payload: {
          key: "description",
          value: "",
        },
      });
    }
  }, [member, generatePassword, user, dispatchUser]);

  // --- --- ---

  const isValid = () => {
    return Boolean(user?.email && user?.description);
  };

  // --- --- ---

  const handleChangeState = (event, value) => {
    if ((!value || value === null) && event.target.value !== null) {
      value = event.target.value;
    }

    dispatchUser({
      type: "update",
      payload: { key: event.target.name, value: value },
    });
  };

  // --- --- ---

  const handleError = (err) => {
    console.warn("An error occurred:", err);
    const errorMessage = getErrorMessage(err);
    enqueueSnackbar(errorMessage, { preventDuplicate: true, variant: "error" });
  };

  const handleSaveClick = async () => {
    setIsLoading(true);
    try {
      if (!isEdit) {
        const _newUser = {
          ...user,
          member_id: user?.id,
          is_active: true,
        };
        await UsersApiHelper.createUser(_newUser, {});
      } else {
        const api = new ApiHelper({
          baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
          config: {},
          sessionManager: _sessionManager,
          // No caching:
          _instance: null,
        });

        const _newUser = {
          user_name: user?.email,
          password: user?.password,
        };
        await api.callPost("User/ResetPassword", _newUser, {});
      }
      onClose();
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>{isEdit ? "Edit" : "Add"} User</DialogTitle>

      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Description"
                name="description"
                isRequired={true}
                defaultValue={user.description || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Email"
                id="new-user-email"
                name="email"
                isRequired={true}
                defaultValue={user.email || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControl fullWidth={true}>
              <TextField
                label="Choose a New Password"
                name="password"
                isRequired={true}
                value={user.password || ""} // Use `value` instead of `defaultValue` with `useState`
                variant="outlined"
                type="text"
                onChange={handleChangeState}
                autoComplete="off"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  autoComplete: "off",
                  style: { WebkitTextSecurity: showPassword ? "none" : "disc" },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon color="text.primary" />
                        ) : (
                          <VisibilityIcon color="text.primary" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          <Button variant="contained" color="secondary" autoFocus onClick={onClose}>
            <CloseOutlined sx={{ mr: 1 }} />
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSaveClick} disabled={!isValid()}>
            <SaveOutlined sx={{ mr: 1 }} />
            Save &amp; Send
          </Button>
        </Stack>
      </DialogActions>
    </>
  );
};
